import React, { useContext } from 'react';
import {
  Button,
  Image,
  Text,
  Title3,
} from '@fluentui/react-components';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import type { InvitationValidationReason } from '@bingads-webui-campaign-react/accept-invitation-api';
import { useAcceptInvitationStyles } from '../styles';
import HeaderLogo from '../../../media/microsoft-advertising-logo-idp-header.png';

export type ActivationInvitationErrorProps = {
  reason: InvitationValidationReason;
  // "Go back" — sign the current session out and return to the invitation choice view.
  onBackToInvitation: () => void;
};

// Post-auth friendly error page for a terminal invitation-code failure (expired / user-limit /
// inactive / invalid) surfaced by Validate or Activate. Copy is kept identical to the pre-auth
// InvitationErrorView so a code that goes invalid between the pre-auth landing and the post-auth
// activation reads the same, instead of the raw CCMT fault or the generic "Something went wrong".
export const ActivationInvitationError: React.FC<ActivationInvitationErrorProps> = ({ reason, onBackToInvitation }) => {
  const i18n = useContext(I18nContext) as any;
  const classes = useAcceptInvitationStyles();

  let title;
  let description;
  switch (reason) {
    case 'Expired':
      title = _TL_('This invitation has expired');
      description = _TL_('This invitation link is no longer valid because it has expired. Invitation links are valid for 30 days from the date they\'re sent. To get access, ask the person who invited you to send a new invitation.');
      break;
    case 'UsersLimitReached':
      title = _TL_('This account has reached its user limit');
      description = _TL_('This account already has the maximum number of users, so you can\'t be added right now. Contact the account admin for help.');
      break;
    case 'Inactive':
      title = _TL_('This ad account is inactive');
      description = _TL_('Reach out to the person who invited you for next steps or contact support.');
      break;
    // Any unrecognized reason renders the same "invitation unavailable" copy as Invalid.
    case 'Invalid':
    default:
      title = _TL_('This link no longer works');
      description = _TL_('You may have already accepted this invitation, or it has been canceled. Reach out to the person who invited you.');
      break;
  }

  return (
    <div className={classes.centeredPage}>
      <div
        className={classes.errorCard}
        role="region"
        aria-labelledby="activation-invitation-error-title"
        aria-describedby="activation-invitation-error-description"
      >
        <Image className={classes.brandLogo} src={HeaderLogo} alt={i18n.getString(_TL_('Microsoft Advertising'))} />

        <Title3 id="activation-invitation-error-title" className={classes.errorTitle}>
          {i18n.getString(title)}
        </Title3>

        <div id="activation-invitation-error-description" className={classes.errorBody}>
          <Text block>{i18n.getString(description)}</Text>
        </div>

        <div className={classes.errorActions}>
          <Button
            className="invitation-error-go-back"
            appearance="primary"
            onClick={onBackToInvitation}
          >
            {i18n.getString(_TL_('Go back'))}
          </Button>
        </div>
      </div>
    </div>
  );
};
